import { Controller } from "@hotwired/stimulus"

const MAX_TABS = 6

// Manages multiple concurrent search tabs via localStorage.
// Each tab stores a query + label. Switching tabs loads the stored query.
export default class extends Controller {
  static targets = ["list"]
  static values = { storageKey: { type: String, default: "siem_tabs_v1" } }

  #tabs = []
  #activeId = null
  #boundSync = null
  #boundOpenTab = null

  connect() {
    this.#load()
    const openTabLabel = new URLSearchParams(window.location.search).get("open_tab")
    if (this.#tabs.length === 0) {
      this.#bootstrapTab(openTabLabel)
    } else if (openTabLabel) {
      this.#applyOpenTab(openTabLabel)
    }
    this.#render()

    this.#boundSync = () => this.#syncActiveTab()
    document.addEventListener("turbo:submit-end", this.#boundSync)

    this.#boundOpenTab = e => this.#openTabFromEvent(e)
    window.addEventListener("siem:open-tab", this.#boundOpenTab)
  }

  disconnect() {
    document.removeEventListener("turbo:submit-end", this.#boundSync)
    window.removeEventListener("siem:open-tab", this.#boundOpenTab)
  }

  addTab(event) {
    event.preventDefault()
    if (this.#tabs.length >= MAX_TABS) return
    this.#syncActiveTab()
    const id = `${Date.now()}`
    this.#tabs.push({ id, label: "New search", query: "" })
    this.#activeId = id
    this.#save()
    this.#render()
    // Clear query box for fresh start
    const textarea = this.#esqlTextarea()
    if (textarea) { textarea.value = ""; textarea.dispatchEvent(new Event("input", { bubbles: true })) }
  }

  switchTab(event) {
    const id = event.currentTarget.closest("[data-tab-id]")?.dataset.tabId
    if (!id || id === this.#activeId) return
    this.#syncActiveTab()
    this.#activeId = id
    this.#save()
    this.#render()
    const tab = this.#tabs.find(t => t.id === id)
    if (!tab) return
    const textarea = this.#esqlTextarea()
    if (textarea) {
      textarea.value = tab.query
      textarea.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  closeTab(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.tabId
    const idx = this.#tabs.findIndex(t => t.id === id)
    if (idx < 0) return
    this.#tabs.splice(idx, 1)
    if (this.#activeId === id) {
      this.#activeId = this.#tabs[Math.max(0, idx - 1)]?.id || null
      if (this.#activeId) {
        const tab = this.#tabs.find(t => t.id === this.#activeId)
        const textarea = this.#esqlTextarea()
        if (textarea && tab) {
          textarea.value = tab.query
          textarea.dispatchEvent(new Event("input", { bubbles: true }))
        }
      }
    }
    this.#save()
    this.#render()
  }

  #bootstrapTab(label = null) {
    const query = this.#esqlTextarea()?.value || ""
    const id = `${Date.now()}`
    this.#tabs = [{ id, label: label || this.#label(query) || "Search 1", query }]
    this.#activeId = id
    this.#save()
  }

  #applyOpenTab(label) {
    const existing = this.#tabs.find(t => t.label === label)
    if (existing) {
      this.#activeId = existing.id
      this.#save()
      return
    }
    if (this.#tabs.length >= MAX_TABS) return
    const query = this.#esqlTextarea()?.value || ""
    const id = `${Date.now()}`
    this.#tabs.push({ id, label, query })
    this.#activeId = id
    this.#save()
  }

  #syncActiveTab() {
    if (!this.#activeId) return
    const tab = this.#tabs.find(t => t.id === this.#activeId)
    if (!tab) return
    const query = this.#esqlTextarea()?.value || ""
    tab.query = query
    tab.label = this.#label(query) || tab.label
    this.#save()
    this.#render()
  }

  #load() {
    try {
      const d = JSON.parse(localStorage.getItem(this.storageKeyValue) || "{}")
      this.#tabs = Array.isArray(d.tabs) ? d.tabs : []
      this.#activeId = d.activeId || null
    } catch {
      this.#tabs = []
      this.#activeId = null
    }
  }

  #save() {
    localStorage.setItem(this.storageKeyValue, JSON.stringify({ tabs: this.#tabs, activeId: this.#activeId }))
  }

  #render() {
    if (!this.hasListTarget) return
    this.listTarget.innerHTML = this.#tabs.map(tab => `
      <span class="siem-tab${tab.id === this.#activeId ? " active" : ""}"
            data-tab-id="${tab.id}"
            data-action="click->accounts--logs--query-tabs#switchTab">
        <span class="siem-tab-label" title="${this.#esc(tab.query)}">${this.#esc(tab.label || "Search")}</span>
        ${this.#tabs.length > 1 ? `<span class="siem-tab-close" data-tab-id="${tab.id}" data-action="click->accounts--logs--query-tabs#closeTab" aria-label="Close">×</span>` : ""}
      </span>`).join("") +
      (this.#tabs.length < MAX_TABS
        ? '<button class="siem-tab-add" type="button" title="New tab" data-action="click->accounts--logs--query-tabs#addTab">+</button>'
        : "")
  }

  #openTabFromEvent(e) {
    const query = e.detail?.query
    if (!query) return
    if (this.#tabs.length >= MAX_TABS) return
    this.#syncActiveTab()
    const id = `${Date.now()}`
    const label = this.#label(query) || "Pythia query"
    this.#tabs.push({ id, label, query })
    this.#activeId = id
    this.#save()
    this.#render()
    const textarea = this.#esqlTextarea()
    if (textarea) {
      textarea.value = query
      textarea.dispatchEvent(new Event("input", { bubbles: true }))
      const form = textarea.closest("form")
      if (form) form.requestSubmit()
    }
  }

  #esqlTextarea() {
    return document.querySelector("textarea[data-controller~='accounts--logs--esql-editor']")
      ?? document.querySelector("textarea[name='query']")
  }

  #label(query) {
    if (!query) return ""
    const where = query.match(/where\s+(.{1,25})/i)
    if (where) return where[1].trim()
    return query.trim().slice(0, 24) || ""
  }

  #esc(s) {
    return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
